// 色の定数
const COLORS = {
  // プライマリー色
  primary: '#09529f',
  primaryDark: '#002348',
  
  // 状態色
  success: '#28a745',
  successBg: '#d4edda',
  
  danger: '#dc3545',
  dangerBg: '#f8d7da',
  
  warning: '#f0ad4e',
  warningBg: '#fff3cd',
  warningBorder: '#ffeaa7',
  warningText: '#856404',
  
  // 基本色
  white: '#fff',
  lightGray: '#f8f9fa',
  borderGray: '#dee2e6',
  textGray: '#666',
  appBackground: '#F0F8FF',
};

// レイアウト定数
const LAYOUT = {
  borderRadius: '8px',
  maxWidth: '800px',
  
  spacing: {
    xs: '8px',
    sm: '15px',
    md: '20px',
    lg: '30px',
    xl: '40px',
  },
  
  fontSize: {
    sm: '12px',
    base: '14px',
    md: '16px',
    lg: '18px',
    xl: '28px',
  },
  
  fontWeight: {
    normal: 'normal',
    bold: 'bold',
  },
};

// ブレークポイント
const BREAKPOINTS = {
  mobile: '768px'
};

/**
 * 基本レイアウトスタイル
 */
export const layoutStyles = {
  container: {
    minHeight: '100vh',
    backgroundColor: COLORS.appBackground,
    padding: LAYOUT.spacing.md,
  },
  
  content: {
    maxWidth: LAYOUT.maxWidth,
    margin: '0 auto',
    backgroundColor: COLORS.white,
    borderRadius: LAYOUT.borderRadius,
    padding: LAYOUT.spacing.xl,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    border: `2px solid ${COLORS.primary}`,
  },
  
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: LAYOUT.spacing.lg,
    borderBottom: `3px solid ${COLORS.primary}`,
    paddingBottom: LAYOUT.spacing.sm,
  },
  
  title: {
    fontSize: LAYOUT.fontSize.xl,
    fontWeight: LAYOUT.fontWeight.bold,
    color: COLORS.primary,
    margin: 0,
  },
};

/**
 * 通知エリアスタイル
 */
export const noticeStyles = {
  notice: {
    backgroundColor: COLORS.warningBg,
    border: `1px solid ${COLORS.warningBorder}`,
    borderRadius: '4px',
    padding: '16px',
    marginBottom: LAYOUT.spacing.lg,
    fontSize: LAYOUT.fontSize.base,
    color: COLORS.warningText,
  },
};

/**
 * フォームセクションスタイル
 */
export const formStyles = {
  formSection: {
    marginBottom: LAYOUT.spacing.lg,
  },
  
  sectionTitle: {
    fontSize: LAYOUT.fontSize.lg,
    fontWeight: LAYOUT.fontWeight.bold,
    color: COLORS.primary,
    marginBottom: '16px',
    borderBottom: `2px solid ${COLORS.primary}`,
    paddingBottom: LAYOUT.spacing.xs,
  },
  
  formGrid: {
    display: 'grid',
    gap: LAYOUT.spacing.md,
  },
  
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  
  label: {
    marginBottom: LAYOUT.spacing.xs,
    fontWeight: LAYOUT.fontWeight.bold,
    fontSize: LAYOUT.fontSize.base,
    color: '#333',
  },
  
  required: {
    color: COLORS.danger,
    marginLeft: '2px',
  },
};

/**
 * 入力フィールドスタイル
 */
export const inputStyles = {
  input: {
    width: '100%',
    padding: '12px 16px',
    border: `1px solid ${COLORS.borderGray}`,
    borderRadius: '4px',
    fontSize: LAYOUT.fontSize.md,
    boxSizing: 'border-box',
    transition: 'border-color 0.2s ease',
    outline: 'none',
  },
  
  select: {
    width: '100%',
    padding: '12px 16px',
    border: `1px solid ${COLORS.borderGray}`,
    borderRadius: '4px',
    fontSize: LAYOUT.fontSize.md,
    boxSizing: 'border-box',
    backgroundColor: COLORS.white,
    cursor: 'pointer',
  },
  
  inputError: {
    borderColor: COLORS.danger,
    borderWidth: '2px',
    boxShadow: '0 0 0 2px rgba(220, 53, 69, 0.1)',
  },
  
  error: {
    color: COLORS.danger,
    fontSize: LAYOUT.fontSize.sm,
    marginTop: '6px',
    fontWeight: LAYOUT.fontWeight.bold,
  },
  
  securityInfo: {
    fontSize: LAYOUT.fontSize.sm,
    color: COLORS.textGray,
    marginTop: '6px',
    lineHeight: '1.4',
    textAlign: 'center',
    maxWidth: '300px',
  },
};

/**
 * ボタンスタイル
 */
export const buttonStyles = {
  backButton: {
    backgroundColor: '#09529f',
    color: COLORS.white,
    padding: '10px 20px',
    border: 'none',
    borderRadius: '4px',
    fontSize: LAYOUT.fontSize.base,
    fontWeight: LAYOUT.fontWeight.bold,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap',
  },
  
  buttonContainer: {
    display: 'flex',
    gap: LAYOUT.spacing.sm,
    justifyContent: 'center',
    marginTop: LAYOUT.spacing.xl,
    paddingTop: LAYOUT.spacing.md,
    borderTop: `2px solid ${COLORS.borderGray}`,
  },
  
  button: {
    padding: '12px 30px',
    border: 'none',
    borderRadius: '4px',
    fontSize: LAYOUT.fontSize.md,
    fontWeight: LAYOUT.fontWeight.bold,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    minWidth: '120px',
  },
  
  primaryButton: {
    backgroundColor: COLORS.primary,
    color: COLORS.white,
  },
  
  primaryButtonDisabled: {
    backgroundColor: COLORS.textGray,
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  
  secondaryButton: {
    backgroundColor: COLORS.textGray,
    color: COLORS.white,
  },
};

/**
 * ホバー効果のスタイル関数
 */
export const hoverEffects = {
  // ボタンホバー効果を適用
  applyBackButtonHover: (element) => {
    element.style.backgroundColor = COLORS.primaryDark;
  },
  
  resetBackButtonHover: (element) => {
    element.style.backgroundColor = '#09529f';
  },
  
  applySecondaryButtonHover: (element) => {
    element.style.backgroundColor = COLORS.primaryDark;
  },
  
  resetSecondaryButtonHover: (element) => {
    element.style.backgroundColor = COLORS.textGray;
  },
  
  applyPrimaryButtonHover: (element, isSubmitting) => {
    if (!isSubmitting) {
      element.style.backgroundColor = COLORS.primaryDark;
    }
  },
  
  resetPrimaryButtonHover: (element, isSubmitting) => {
    if (!isSubmitting) {
      element.style.backgroundColor = COLORS.primary;
    }
  },
};

/**
 * 全スタイルを統合したオブジェクト（従来の方式との互換性のため）
 */
export const creditCardStyles = {
  ...layoutStyles,
  ...noticeStyles,
  ...formStyles,
  ...inputStyles,
  ...buttonStyles,
};

/**
 * スタイル適用のユーティリティ関数
 */
export const styleUtils = {
  // 入力フィールドのスタイル取得
  getInputStyle: (hasError) => ({
    ...inputStyles.input,
    ...(hasError ? inputStyles.inputError : {}),
  }),
  
  // ボタンのスタイル取得
  getButtonStyle: (variant, isDisabled = false) => {
    let style = { ...buttonStyles.button };
    
    switch (variant) {
      case 'primary':
        style = { ...style, ...buttonStyles.primaryButton };
        if (isDisabled) {
          style = { ...style, ...buttonStyles.primaryButtonDisabled };
        }
        break;
      case 'secondary':
        style = { ...style, ...buttonStyles.secondaryButton };
        break;
      default:
        break;
    }
    
    return style;
  },
  
  // セレクトボックスのスタイル取得
  getSelectStyle: (hasError) => ({
    ...inputStyles.select,
    ...(hasError ? inputStyles.inputError : {}),
  }),
};

/**
 * モバイル用スタイル
 */
export const mobileStyles = `
  @media (max-width: ${BREAKPOINTS.mobile}) {
    .credit-card-container,
    .password-change-container {
      padding: 10px !important;
    }
    
    .credit-card-content,
    .password-change-content {
      padding: 15px !important;
      border-radius: 4px !important;
    }
    
    .credit-card-header,
    .password-change-header {
      flex-direction: column !important;
      align-items: stretch !important;
      gap: 15px !important;
      margin-bottom: 20px !important;
    }
    
    .credit-card-title,
    .password-change-title {
      font-size: 20px !important;
    }
    
    .credit-card-back-button,
    .password-change-back-button {
      width: 100% !important;
      text-align: center !important;
    }
    
    .credit-card-notice,
    .password-change-notice {
      font-size: 13px !important;
      padding: 12px !important;
    }
    
    /* テーブルをカード形式に変換 */
    .credit-card-table,
    .password-change-table {
      display: block !important;
      box-shadow: none !important;
    }
    
    .credit-card-table tbody,
    .password-change-table tbody {
      display: block !important;
    }
    
    .credit-card-table-row,
    .password-change-table-row {
      display: block !important;
      margin-bottom: 15px !important;
      border: 1px solid ${COLORS.borderGray} !important;
      border-radius: 8px !important;
      padding: 12px !important;
      background-color: #fff !important;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important;
    }
    
    .credit-card-table-th,
    .credit-card-table-td,
    .password-change-table-th,
    .password-change-table-td {
      display: block !important;
      width: 100% !important;
      padding: 0 !important;
      border: none !important;
      text-align: left !important;
      background-color: transparent !important;
    }
    
    .credit-card-table-th,
    .password-change-table-th {
      padding-bottom: 8px !important;
      font-size: 13px !important;
      font-weight: bold !important;
      color: #333 !important;
    }
    
    .credit-card-table-td,
    .password-change-table-td {
      padding-top: 0 !important;
    }
    
    .credit-card-table-td input,
    .credit-card-table-td select,
    .password-change-table-td input {
      font-size: 16px !important;
      padding: 12px !important;
    }
    
    /* 有効期限の表示を調整 */
    .credit-card-expiry-row {
      flex-wrap: wrap !important;
    }
    
    .credit-card-expiry-row select {
      flex: 1 !important;
      min-width: 100px !important;
    }
    
    .credit-card-button-container,
    .password-change-button-container {
      flex-direction: column-reverse !important;
      gap: 10px !important;
      margin-top: 30px !important;
    }
    
    .credit-card-button,
    .password-change-button {
      width: 100% !important;
      padding: 14px 20px !important;
      font-size: 16px !important;
      min-width: auto !important;
    }
    
    /* セキュリティ注意事項 */
    .password-security-note {
      font-size: 12px !important;
      padding: 12px !important;
    }
    
    /* ログイン情報表示 */
    .password-user-info {
      flex-direction: column !important;
      align-items: flex-start !important;
      gap: 5px !important;
    }
  }
`;

// スタイルシートに追加
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = mobileStyles;
  document.head.appendChild(styleElement);
}

// デフォルトエクスポート
export default creditCardStyles;